import { fetchMaUserConfiguration, savePinnedBooks } from '@/lib/ma-user-config-api';
import { computeTogglePinned } from '@/lib/pinned-books';
import { toast } from 'sonner';

function pinnedIdsEqual(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((id, index) => id === b[index]);
}

type StoreStatus = 'idle' | 'loading' | 'ready';

let status: StoreStatus = 'idle';
let pinnedIds: string[] = [];
let lastSavedIds: string[] = [];
let loadPromise: Promise<void> | null = null;
let persistInFlight: Promise<void> | null = null;
let persistAgain = false;

const listeners = new Set<() => void>();

function emit() {
    for (const listener of listeners) {
        listener();
    }
}

export function subscribePinnedBooks(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function getPinnedBookIdsSnapshot(): string[] {
    return pinnedIds;
}

export function getPinnedBooksServerSnapshot(): string[] {
    return pinnedIds;
}

export function arePinnedBooksLoading(): boolean {
    return status === 'loading';
}

export function getPinnedBooksLoadingServerSnapshot(): boolean {
    return false;
}

/** Seed from a loader when the store has not loaded yet. No-ops once ready. */
export function hydratePinnedBooks(ids: string[]) {
    if (status !== 'idle') {
        return;
    }
    pinnedIds = ids;
    lastSavedIds = ids;
    status = 'ready';
    emit();
}

export function ensurePinnedBooksLoaded(): Promise<void> {
    if (status === 'ready') {
        return Promise.resolve();
    }
    if (loadPromise) {
        return loadPromise;
    }

    status = 'loading';
    loadPromise = Promise.resolve()
        .then(() => fetchMaUserConfiguration())
        .then(config => {
            if (status === 'ready') {
                return;
            }
            pinnedIds = config.pinnedBooks;
            lastSavedIds = config.pinnedBooks;
            status = 'ready';
            emit();
        })
        .catch(() => {
            if (status === 'loading') {
                status = 'idle';
                emit();
            }
        })
        .finally(() => {
            loadPromise = null;
        });

    return loadPromise;
}

async function persistPinnedBooks() {
    if (persistInFlight) {
        persistAgain = true;
        return persistInFlight;
    }

    const run = async () => {
        do {
            persistAgain = false;
            const toSave = pinnedIds;

            try {
                const saved = await savePinnedBooks(toSave);

                if (!pinnedIdsEqual(pinnedIds, toSave)) {
                    persistAgain = true;
                    continue;
                }

                pinnedIds = saved;
                lastSavedIds = saved;
                emit();
            } catch {
                pinnedIds = lastSavedIds;
                emit();
                toast.error('Could not update pinned books', {
                    description: 'Your change was not saved. Try again.',
                });
                return;
            }
        } while (persistAgain);
    };

    persistInFlight = run().finally(() => {
        persistInFlight = null;
    });

    return persistInFlight;
}

export function togglePinnedBook(bookId: string) {
    if (status !== 'ready') {
        void ensurePinnedBooksLoaded();
        return;
    }

    pinnedIds = computeTogglePinned(pinnedIds, bookId);
    emit();
    void persistPinnedBooks();
}

export function isBookPinned(bookId: string): boolean {
    return pinnedIds.includes(bookId);
}

/** Test-only: reset module state between cases. */
export function resetPinnedBooksStoreForTests() {
    status = 'idle';
    pinnedIds = [];
    lastSavedIds = [];
    loadPromise = null;
    persistInFlight = null;
    persistAgain = false;
}
